import { addDays } from 'date-fns';

import { parseAirDateToISO } from '@air/utilities';

import { TEAM_INVITE_NUDGE_INVITE_TRIGGERED, TEAM_INVITE_NUDGE_SIDE_NAV_DISMISSED } from '~/constants/localStorageKeys';
import { appStorage } from '~/utils/Storage';

export const TEAM_INVITE_NUDGE_WINDOW_DAYS = 14;
export const TEAM_INVITE_NUDGE_LAUNCH_DATE = '2026-05-01T16:00:00.000Z';
export const TEAM_INVITE_NUDGE_INVITE_TRIGGERED_EVENT = 'teamInviteNudgeInviteTriggered';

const getInviteTriggeredStorageKey = ({ workspaceId }: { workspaceId: string }) =>
  `${TEAM_INVITE_NUDGE_INVITE_TRIGGERED}/${workspaceId}`;

const getSideNavDismissedStorageKey = ({ workspaceId }: { workspaceId: string }) =>
  `${TEAM_INVITE_NUDGE_SIDE_NAV_DISMISSED}/${workspaceId}`;

const getDate = (value: string): Date | undefined => {
  const date = new Date(parseAirDateToISO(value));

  return Number.isNaN(date.getTime()) ? undefined : date;
};

const getAvailabilityWindowStart = (workspaceCreatedAt: string) => {
  const launchDate = new Date(TEAM_INVITE_NUDGE_LAUNCH_DATE);
  const workspaceCreatedAtDate = getDate(workspaceCreatedAt);

  return workspaceCreatedAtDate && workspaceCreatedAtDate > launchDate ? workspaceCreatedAtDate : launchDate;
};

export const isTeamInviteNudgeWithinWindow = ({
  workspaceCreatedAt,
  now = new Date(),
}: {
  workspaceCreatedAt: string;
  now?: Date;
}) => {
  const startDate = getAvailabilityWindowStart(workspaceCreatedAt);
  const endDate = addDays(startDate, TEAM_INVITE_NUDGE_WINDOW_DAYS);

  return now >= startDate && now < endDate;
};

export const saveTeamInviteNudgeInviteTriggered = ({ workspaceId }: { workspaceId: string }) => {
  try {
    appStorage.setItem(getInviteTriggeredStorageKey({ workspaceId }), 'true');
  } catch {
    // If localStorage is unavailable, invite creation should still succeed.
  }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(TEAM_INVITE_NUDGE_INVITE_TRIGGERED_EVENT, { detail: { workspaceId } }));
  }
};

export const wasTeamInviteNudgeInviteTriggered = ({ workspaceId }: { workspaceId: string }) => {
  try {
    return !!appStorage.getItem(getInviteTriggeredStorageKey({ workspaceId }));
  } catch {
    return false;
  }
};

export const saveSideNavTeamInviteNudgeDismissed = ({ workspaceId }: { workspaceId: string }) => {
  try {
    appStorage.setItem(getSideNavDismissedStorageKey({ workspaceId }), 'true');
  } catch {
    // If localStorage is unavailable, dismissing should still hide the nudge for this render.
  }
};

export const wasSideNavTeamInviteNudgeDismissed = ({ workspaceId }: { workspaceId: string }) => {
  try {
    return !!appStorage.getItem(getSideNavDismissedStorageKey({ workspaceId }));
  } catch {
    return false;
  }
};
